import type { PresenceDoc } from './presenceDocument';

/** What consumers want the server to know. */
export type Desired = {
  subscribed: boolean;
  presence: PresenceDoc | null;
};

/**
 * What we believe the server knows, or what is in flight toward it. `phase` is the public
 * `ChannelState`. Phases that wait on a result are entered optimistically: asking for a token
 * enters `authorizing`, sending subscribe enters `subscribing`.
 *
 * `subscribed` carries the presence document last sent, which is what makes an unchanged document
 * produce no frame. The capability token is not here: it outlives phases, so it is an input.
 */
export type Actual =
  | { phase: 'unsubscribed' }
  | { phase: 'authorizing' }
  | { phase: 'subscribing' }
  | { phase: 'subscribed'; presence: PresenceDoc | null }
  | { phase: 'unsubscribing' };

export type Effect =
  | { do: 'fetchToken' }
  | { do: 'abortToken' }
  | { do: 'dropToken' }
  | { do: 'subscribe'; token: string }
  | { do: 'unsubscribe' }
  | { do: 'setPresence'; document: PresenceDoc | null };

export type PlanInput = {
  connected: boolean;
  desired: Desired;
  actual: Actual;
  /** The cached capability token. It outlives phases, so it is not part of `Actual`. */
  token?: string;
};

export type Plan = { actual: Actual; effects: Effect[] };

const UNSUBSCRIBED: Actual = { phase: 'unsubscribed' };

function stay(actual: Actual): Plan {
  return { actual, effects: [] };
}

/**
 * The whole channel lifecycle, as one pure step from what we want to what we say.
 *
 * Every input to a session — a lease, a release, a presence write, a connection change, an inbound
 * frame — becomes "write one field, then run this". No caller decides its own next send, so there
 * is one place that answers "we want to be subscribed and we are not yet".
 */
export function plan({ connected, desired, actual, token }: PlanInput): Plan {
  if (!connected) {
    // The socket took the subscription with it, and the token that was minted to join goes too.
    if (actual.phase === 'unsubscribed' && token === undefined) return stay(actual);
    return { actual: UNSUBSCRIBED, effects: [{ do: 'abortToken' }, { do: 'dropToken' }] };
  }

  if (!desired.subscribed) {
    switch (actual.phase) {
      case 'authorizing':
        // Nothing reached the server, so there is nothing to leave.
        return { actual: UNSUBSCRIBED, effects: [{ do: 'abortToken' }] };
      case 'subscribing':
      case 'subscribed':
        return { actual: { phase: 'unsubscribing' }, effects: [{ do: 'unsubscribe' }] };
      default:
        return stay(actual);
    }
  }

  switch (actual.phase) {
    case 'unsubscribed':
      return token
        ? { actual: { phase: 'subscribing' }, effects: [{ do: 'subscribe', token }] }
        : { actual: { phase: 'authorizing' }, effects: [{ do: 'fetchToken' }] };
    case 'subscribed':
      // The phase does not move, but the recorded last-sent document has to, or the effect repeats.
      if (actual.presence === desired.presence) return stay(actual);
      return {
        actual: { ...actual, presence: desired.presence },
        effects: [{ do: 'setPresence', document: desired.presence }],
      };
    default:
      // `authorizing`, `subscribing` and `unsubscribing` are all waiting on a result. Asking again
      // is what the deadline is for.
      return stay(actual);
  }
}
